import { AjaxHelper, IPaginatedList, pluralize } from "@samc/common";
import { MenuItemDef } from "ag-grid-community";
import { SimpleInternalDataImportModal, AllocateLoansToDealModal, RemoveLoansFromDealModal,
  RunPricingModal, UpdateContractNameModal, OverrideInternalDataModal,
  AddInternalCommentToLoansModal, ReviewDiligenceExceptionModal, ServicerLoanIdOverrideModal
} from "./Components/Modals";
import { IDeal, IAddLoansToDealRequest, IValidationError, IPackageVersion, IKickOffLoansRequest, IDealListResponse, IRunPricingParameter, IUpdateContractName, IServicerLoanIdOverride } from "./Interfaces";
import { handleErrorResponse, gotoPost } from "./utils";
import { getCurrentInstance } from "vue";

export interface ILoanGridActionOptions {
  /**
   * The entitlement to allocate loans to deals
   */
  readonly entitledToAllocateLoansToDeals: boolean;

  /**
   * The entitlement to remove loans from deals
   */
  readonly entitledToRemoveLoansFromDeals: boolean;

  /**
   * The entitlement to liquidate loans
   */
  readonly entitledToLiquidateLoans: boolean;

  /**
   * The entitlement to reinstate loans
   */
  readonly entitledToReinstateLoans: boolean;

  /**
   * The entitlement to run pricing import
   */
  readonly entitledToRunPricing: boolean;

  /**
   * The entitlement to import internal data
   */
  readonly entitledToImportInternal: boolean;

  /**
   * The current user logged in
   */
  readonly currentUser: string;

  /**
   * The verification token for the user
   */
  readonly verificationToken: string;

  /**
   * The data import url for going through the data import process
   */
  readonly dataImportUrl: string;

  /**
   * The internal data url for importing internal data
   */
  readonly internalDataUrl: string;

  /**
   * The record type selected in grid for tab
   */
  readonly tabbedRecordTypes: string[];

  /**
   * The tab index of selected
   */
  readonly tabIndex: number;
}

export class LoanGridActionHandler {
  private _instance: { proxy: Vue } | null = null;
  /**
   * The latest event id for the loans
   */
  public latestEventId: number | null = null;

  /**
   * Constructor for the loan grid action handler
   * 
   * @param _options The options for the loan grid action handler
   */
  public constructor(private _options: ILoanGridActionOptions) {
    this._instance = getCurrentInstance();

  }

  /**
   * Helper method that will be called when the actions for the context and action button menus in the LS grid are called
   * 
   * @param data The current set of data points that are being used in the LS grid
   */
  public gridActions(data: any[], rootKeys: any[]): (MenuItemDef | 'separator')[] {

    //TODO: Handle pulling parentPK when subdomain is loaded
    const primaryKeys = data?.map(x => typeof (x) === 'number' ? x : x.primaryKey) as number[];

    const actions: (MenuItemDef | 'separator')[] = [];
    actions.push('separator');

    this._instance?.proxy.$log(`###### selected tab index ${this._options.tabIndex} and record type ${this._options.tabbedRecordTypes[this._options.tabIndex]}.`);

    // only show for diligence exceptions
    if (this._options.tabbedRecordTypes[this._options.tabIndex] === 'diligence exceptions')
    {
      actions.push({
        name: 'Review Exception',
        action: this.onReviewDDExceptions.bind(this, primaryKeys),
        key: 'review-exception'
      } as MenuItemDef & { key: string });
    } else if (this._options.tabbedRecordTypes[this._options.tabIndex] === 'task') {
      return []; // no actions for task subdomain since it is not supported
    }

    this._instance?.proxy.$log(`Tab index ${this._options.tabIndex} domain type set to: ${this._options.tabbedRecordTypes[this._options.tabIndex]}`);

    actions.push({
      name: 'Add Internal Comment',
      action: this.onAddInternalCommentData.bind(this, rootKeys !== undefined && rootKeys.length > 0 ? rootKeys : primaryKeys),
      disabled: !this._options.entitledToImportInternal,
      key: 'add-internal-comment'
    } as MenuItemDef & { key: string });

    actions.push({
      name: 'Quick Internal Import',
      action: this.onImportInternalData.bind(this),
      disabled: !this._options.entitledToImportInternal,
      key: 'quick-internal-import'
    } as MenuItemDef & { key: string });

    if (!data || !data.length) return this.addKeysToMenuItems(actions);
    
    // All loan actions must be invoked with LoanIds and not subdomain Ids.
    const allActions = actions.concat(this.getLoansActions(data, rootKeys));
    return this.addKeysToMenuItems(allActions);
  }

  /**
   * Helper method to ensure all menu items have unique keys for React rendering
   * This helps prevent the "Each child in a list should have a unique key prop" warning
   */
  private addKeysToMenuItems(items: (MenuItemDef | 'separator')[]): (MenuItemDef | 'separator')[] {
    return items.map((item, index) => {
      if (item === 'separator') {
        // For separators, we can't modify them directly, but the React component
        // should use the array index as a key. However, to be safe, we ensure
        // menu items have unique keys that can be used.
        return item;
      }

      // Ensure menu items have unique keys
      const menuItem = item as MenuItemDef & { key?: string };
      if (!menuItem.key && menuItem.name) {
        menuItem.key = `menu-item-${menuItem.name.toLowerCase().replace(/\s+/g, '-')}-${index}`;
      }

      return menuItem;
    });
  }

  private async onReviewDDExceptions(exceptionIdList: number[]) {
    if (!this._options.entitledToImportInternal) {
      window.$alert.error('You are not entitlemented to Add internal comments data.');
      return;
    }

    if ((exceptionIdList?.length ?? 0) === 0) return;

    window.$modal({
      title: 'Review Exception',
      component: ReviewDiligenceExceptionModal,
      width: 550,
      props: {
        verificationToken: this._options.verificationToken,
        exceptionIdList: exceptionIdList,
      }
    });
  }

  /**
   * The action to perform when the user selects the Add Internal Comment context menu option
   */
  private async onAddInternalCommentData(loanIdsList: number[]) {
    if (!this._options.entitledToImportInternal) {
      window.$alert.error('You are not entitlemented to Add internal comments data.');
      return;
    }

    if ((loanIdsList?.length ?? 0) === 0) return;

    window.$modal({
      title: 'Add Internal Comment',
      component: AddInternalCommentToLoansModal,
      width: 550,
      props: {
        verificationToken: this._options.verificationToken,
        user: this._options.currentUser,
        url: this._options.internalDataUrl,
        loanIdsList: loanIdsList
      }
    });
  }

  /**
   * Helper method meant to get a list of actions for the loans in the grid
   * 
   * @param data The current list of loans for the actions
   * @returns Will return a list of actions for the loans
   */
  private getLoansActions(data: any[], rootKeys: any[]): (MenuItemDef | 'separator')[] {
    //Use parent domain primary keys/ids for all Loan actions when subdomain is loaded
    const loanIds = rootKeys !== undefined && rootKeys.length > 0 ? rootKeys : data.map(x => typeof (x) === 'number' ? x : x.primaryKey) as number[];

    const actions: (MenuItemDef | 'separator')[] = [
      
      'separator',
      {
        name: 'Allocate to Deal',
        action: this.allocateLoansToDeal.bind(this, loanIds),
        disabled: !this._options.entitledToAllocateLoansToDeals,
        key: 'allocate-to-deal'
      } as MenuItemDef & { key: string },
      {
        name: 'Remove from Deal',
        action: this.removeLoans.bind(this, loanIds),
        disabled: !this._options.entitledToRemoveLoansFromDeals,
        key: 'remove-from-deal'
      } as MenuItemDef & { key: string },
      'separator',
      {
        name: 'Liquidate Loans',
        action: this.liquidateLoans.bind(this, loanIds),
        disabled: !this._options.entitledToLiquidateLoans,
        key: 'liquidate-loans'
      } as MenuItemDef & { key: string },
      {
        name: 'Reinstate Loans',
        action: this.reinstateLoans.bind(this, loanIds),
        disabled: !this._options.entitledToReinstateLoans,
        key: 'reinstate-loans'
      } as MenuItemDef & { key: string },
      'separator',
      {
        name: 'Run Pricing API',
        action: this.runLoansPricing.bind(this, loanIds),
        disabled: !this._options.entitledToRunPricing,
        key: 'run-pricing-api'
      } as MenuItemDef & { key: string },
      {
        name: 'Update Contract Name',
        action: this.updateContractName.bind(this, loanIds),
        key: 'update-contract-name'
      } as MenuItemDef & { key: string },
      'separator',
      {
        name: 'Bulk Internal Update',
        action: this.onOverrideInternalDataModal.bind(this, loanIds),
        disabled: !this._options.entitledToImportInternal,
        key: 'bulk-internal-update'
      } as MenuItemDef & { key: string },
      {
        name: 'Clear Internal Data', 
        icon: '<i class="mdi mdi-broom"></i>',
        action: this.clearInternalData.bind(this, loanIds),
        disabled: !this._options.entitledToImportInternal,
        key: 'clear-internal-data'
      } as MenuItemDef & { key: string },
      {
        name: 'Servicer Loan ID Override',
        action: this.onServicerLoanIdOverride.bind(this, loanIds),
        disabled: loanIds.length !== 1,
        key: 'servicer-loan-id-override'
      } as MenuItemDef & { key: string },
      {
        name: 'Survivorship Merge',
        action: this.onSurvivorshipMerge.bind(this, loanIds),
        key: 'survivorship-merge'
      } as MenuItemDef & { key: string }
    ];

    return actions;
  }

  /**
   * The action to perform when the user selects the Quick Internal Import option
   */
  private async onImportInternalData() {
    if (!this._options.entitledToImportInternal) {
      window.$alert.error("You don't have the entitlement required to Import internal data.");
      return;
    }

    window.$modal({
      title: 'Quick Internal Import',
      component: SimpleInternalDataImportModal,
      width: 550,
      props: {
        verificationToken: this._options.verificationToken,
        user: this._options.currentUser,
        dataImportUrl: this._options.dataImportUrl,
        documentUrl: this._options.dataImportUrl,
        url: this._options.internalDataUrl
      }
    });
  }

  /**
   * The action to perform when the user selects the Allocate to Deal option
   * 
   * @param loanIds The list of loan ids to allocate to a deal
   */
  private async allocateLoansToDeal(loanIds: number[]) {
    if ((loanIds?.length ?? 0) === 0) return;

    if (!this._options.entitledToAllocateLoansToDeals) {
      window.$alert.error("You don't have the entitlement required to allocate loans to a Deal.");
      return;
    }

    const helper = new AjaxHelper('/InventoryBrowser/Browser', this._options.verificationToken, '', null);

    window.$loading('Retrieving eligible Deals');
    let eligibleDeals: IPaginatedList<IDeal>;
    try {
      eligibleDeals = await helper.postData<IPaginatedList<IDeal>>('', { loanIds: loanIds }, { handler: 'GetEligibleDeals' });
    }catch (e: any) {
      handleErrorResponse(e, window.$alert, window.$log);
      return;
    } finally {
      window.$loading();
    }

    if (!eligibleDeals.fullCount) {
      window.$alert.warning(`The selected ${pluralize(loanIds.length, 'Loans are', 'Loan is')} already allocated or ineligible for allocation.`, '', true);
      return;
    }

    const loansStr = pluralize(loanIds.length, 'Loans', 'Loan');
    const request = await window.$modal<IAddLoansToDealRequest>({
      title: `Allocate ${loansStr} to Deal`,
      component: AllocateLoansToDealModal,
      width: 450,
      props: { loanIds: loanIds, deals: eligibleDeals.items }
    });
    if (request === undefined) return;

    try {
      const buyersValidationResult = await helper.postData<IValidationError>('', {loanIds: loanIds}, { dealId: request.dealId, handler: 'VerifyLoansForDeal' });
      
      if (buyersValidationResult) {
        const confirmResult = await window.$confirm({
          button: { text: 'Continue', data: true },
          link: { text: 'Cancel', data: false },
          title: buyersValidationResult.error,
          type: 'warning'
        });

        if (!confirmResult) return; // user cancelled allocation operation
      }
    }catch (e: any) {
      handleErrorResponse(e, window.$alert, window.$log);
      return;
    }
    
    window.$loading(`Allocating ${loanIds.length} ${loansStr} to Deal`);

    try {
      await helper.postData<IPackageVersion>('', request, { handler: 'AllocateLoansToDeal' })

      window.$loading();
      await window.$alert.success(`${loanIds.length} ${loansStr} allocated to Deal.`);
      location.reload();
    } catch (e: any) {
      window.$loading();
      handleErrorResponse(e, window.$alert, window.$log);
    } finally {
      window.$loading();
    }
  }

  /**
   * The action to perform when the user selects the Remove from Deal option
   */
  private async removeLoans(loanIds: number[]) {
    if ((loanIds?.length ?? 0) === 0) return;

    if (!this._options.entitledToRemoveLoansFromDeals) {
      window.$alert.error("You don't have the entitlement required to remove loans from a deal.");
      return;
    }

    const dealIds: Set<string> = new Set<string>(); // track unique Deals for selected Loans
    for (let i = 0; i < loanIds.length; i++) {
      const deals = await this.getDealsForRemovableLoan(loanIds[i]);
      deals.items.forEach((d) => {
        dealIds.add(d.id);
      });
      if (deals.fullCount === 0) {
        window.$alert.error('Some of the selected Loans are not currently in a non-settled Deal.');
        return;
      }
    }

    if (dealIds.size > 1) {
      var confirmRemoveFromMultipleDeals = await window.$alert<boolean>({
        type: 'warning',
        title: 'Remove from Deal',
        button: { data: true, text: 'Continue' },
        link: { data: false, text: 'Cancel' },
        description: loanIds.length > 1 ?
          'The selected loans are allocated to multiple Deals. Continuing will remove all loans from all non-settled Deals.'
          : 'The selected loan is allocated to multiple Deals. Continuing will remove the loan from all non-settled Deals.'
      });

      if (!confirmRemoveFromMultipleDeals) return;
    }

    // if the user selected a single loan, we will show the RemoveLoansFromDealModal
    let reason = await window.$modal<string>({
      title: `Remove loans from deal`,
      component: RemoveLoansFromDealModal,
      width: 550
    });

    // reason is an optional parameter now, so we can provide a default value
    if (reason === undefined) return;

    const request: IKickOffLoansRequest = {
      kickedOffLoans: loanIds,
      description: reason
    };

    try {
      window.$loading('Removing Loans');
      const helper = new AjaxHelper('/InventoryBrowser/Browser', this._options.verificationToken, '', null);
      const result = await helper.postData<IPackageVersion[]>('', request, { handler: 'RemoveLoansFromDeal' });

      window.$loading();
      await window.$alert.success(`${loanIds.length > 1 ? 'Loans' : 'Loan'} removed from ${result.length} ${result.length > 1 ? 'Deals' : 'Deal'}.`);

      location.reload();
    } catch (e: any) {
      window.$loading();
      handleErrorResponse(e, window.$alert, window.$log);
    } finally {
      window.$loading();
    }
  }

  /**
   * Helper method that will get the deals for a removable loan
   * 
   * @param loanId The current loan to get the deals for
   */
  private async getDealsForRemovableLoan(loanId: number): Promise<IPaginatedList<IDealListResponse>> {
    const helper = new AjaxHelper('/InventoryBrowser/Browser', this._options.verificationToken, '', null);
    return await helper.getData<IPaginatedList<IDealListResponse>>('', { loanId: loanId, status: 'Sourced|Hold|Awarded|Pending Settle|Cancelled', handler: 'DealByLoanId' });
  }

  /**
   * The action to perform when the user selects the Liquidate Loans option
   * 
   * @param loanIds The list of loan ids to liquidate
   */
  private async liquidateLoans(loanIds: any[]) {

    if (loanIds.length < 1) return;

    if (!this._options.entitledToLiquidateLoans) {
      window.$alert.error("You don't have the entitlement required to liquidate loans.");
      return;
    }

    try {
      window.$loading('Liquidating Loans');
      const helper = new AjaxHelper('/InventoryBrowser/Browser', this._options.verificationToken, '', null);
      await helper.postData('', { loanIds: loanIds }, { handler: 'LiquidateLoans' });

      window.$loading();
      await window.$alert.success(`${loanIds.length} ${pluralize(loanIds.length, 'loans were', 'loan was')} updated to a status of "Liquidated".`);
      location.reload();
    } catch (e: any) {
      window.$loading();
      handleErrorResponse(e, window.$alert, window.$log);
    } finally {
      window.$loading();
    }
  }

  /**
   * The action to perform when the user selects the Reinstate Loans option
   * 
   * @param loanIds The list of loan ids to reinstate
   */
  private async reinstateLoans(loanIds: any[]) {

    if (loanIds.length < 1) return;

    if (!this._options.entitledToReinstateLoans) {
      window.$alert.error("You don't have the entitlement required to reinstate loans.");
      return;
    }

    try {
      window.$loading('Reinstating Loans');
      const helper = new AjaxHelper('/InventoryBrowser/Browser', this._options.verificationToken, '', null);
      await helper.postData('', { loanIds: loanIds }, { handler: 'ReinstateLoans' });

      window.$loading();
      await window.$alert.success(`${loanIds.length} ${pluralize(loanIds.length, 'loans were', 'loan was')} updated to a status of "Reinstated".`);
      location.reload();
    } catch (e: any) {
      window.$loading();
      handleErrorResponse(e, window.$alert, window.$log);
    } finally {
      window.$loading();
    }
  }

  /**
   * The action to perform when the user selects the Run Pricing API option
   * 
   * @param loanIds The list of loan ids to run pricing for
   */
  private async runLoansPricing(loanIds: any[]) {

    if (loanIds.length < 1) return;

    if (!this._options.entitledToRunPricing) {
      window.$alert.error("You don't have the entitlement required to Run Pricing API for loans.");
      return;
    }

    try {
      const helper = new AjaxHelper('/InventoryBrowser/Browser', this._options.verificationToken, '', null);

      const parametersMap = new Map();

      parametersMap.set('loanIds', loanIds);

      var runPricingParameters = (await helper.getData<IRunPricingParameter[]>('', { handler: 'RunLoansPricingParameters' }));

      if (runPricingParameters?.length > 0) {
        const parameters = await window.$modal<IRunPricingParameter[]>({
          title: `Run Pricing API for selected loans`,
          component: RunPricingModal,
          width: 550,
          props: {
            loanCount: loanIds.length,
            runPricingParameters: runPricingParameters
          }
        });

        if (parameters === undefined) {
          return;
        } else {
          parameters.forEach(param => {
            if (param.value !== undefined) {
              parametersMap.set(param.name, param.value);
            }
          });
        }
      }

      window.$loading('Submitting Pricing Run for loan(s)');

      const body = Object.fromEntries(parametersMap);

      await helper.postData('', body, { handler: 'RunLoansPricing' });

      window.$loading();

      await window.$alert.success(`${loanIds.length} ${pluralize(loanIds.length, 'loans were', 'loan was')} successfully submitted for Pricing Run.`);

    } catch (e: any) {
      window.$loading();

      handleErrorResponse(e, window.$alert, window.$log);
    } finally {
      window.$loading();
    }
  }

  /**
   * The action to perform when the user selects the Update Contract Name option
   * 
   * @param loanIds The list of loan ids to update the contract name for
   */
  private async updateContractName(loanIds: number[]) {
    if ((loanIds?.length ?? 0) === 0) return;

    const request = await window.$modal<IUpdateContractName>({
      title: `Update Contract Name`,
      component: UpdateContractNameModal,
      width: 550,
      props: { loanIds: loanIds }
    });
    if (request === undefined) return;

    try {
      window.$loading('Updating Contract Name');
      const helper = new AjaxHelper('/InventoryBrowser/Browser', this._options.verificationToken, '', null);
      const response = await helper.postData('', {
        loanIds: loanIds, fieldValue: request.contractName, fieldName: request.contractField
      }, { handler: 'UpdateContract' });

      window.$loading();
      await window.$alert.success(`Updated count: ${response}`);
      location.reload();
    }
    catch (e: any) {
      window.$loading();
      handleErrorResponse(e, window.$alert, window.$log);
    } finally {
      window.$loading();
    }
  }

  /**
   * The action to perform when the user selects the Bulk Internal Update option
   * 
   * @param loans The loans to override internal data for
   */
  private onOverrideInternalDataModal(loans: any[]) {
    if (!this._options.entitledToImportInternal) {
      window.$alert.error("You don't have the entitlement required to Import internal data.");
      return;
    }
    
    window.$modal({
      title: `Update Internal Data`,
      component: OverrideInternalDataModal,
      width: 550,
      props: {
        loans,
        verificationToken: this._options.verificationToken,
        user: this._options.currentUser,
        dataImportUrl: this._options.dataImportUrl,
        documentUrl: this._options.dataImportUrl,
        url: this._options.internalDataUrl
      }
    });
  }

  /**
   * The action to perform when the user selects the Clear Internal Data option
   * 
   * @param loanIds The list of loan ids to clear internal data for
   */
  public async clearInternalData(loanIds: number[]) {
    if (loanIds.length < 1) return;

    if (!this._options.entitledToImportInternal) {
      window.$alert.error("You don't have the entitlement required to Import internal data.");
      return;
    }
    //confirm
    const confirm = await window.$confirm.warning(`You are about to clear all internal data from ${loanIds.length} loans. Proceed?`, 'Clear Internal Data');

    if ((confirm ?? false) === false) return;

    try {
      window.$loading('Clearing Data');
      const helper = new AjaxHelper('/InventoryBrowser/Browser', this._options.verificationToken, '', null);
      const res = await helper.postData('', { loanIds: loanIds }, { handler: 'ClearInternalData' });

      window.$loading();

      interface Counts {
        clearedCount: number,
        eventId: number
      }
      const counts: Partial<Counts> = JSON.parse(JSON.stringify(res));

      await window.$alert.success(`${counts.clearedCount} ${pluralize(counts.clearedCount ?? 0, 'loans were', 'loan was')} updated.`);

      //go to survivorship
      if (counts.clearedCount !== undefined && counts.clearedCount > 0) {
        this.latestEventId = counts.eventId ?? 0;
        await this.askToGotoSurvivorship();
      }
    } catch (e: any) {
      handleErrorResponse(e, window.$alert, window.$log);
    } finally {
      window.$loading();
    }
  }

  /**
   * The action to perform when the user selects the Clear Internal Data option and the operation is successful
   */
  public async askToGotoSurvivorship() {
    const result = await window.$confirm({
      type: 'success',
      title: 'Clear Internal Data success',
      description: 'Would you like to go to survivorship?',
      button: {
        text: 'Go To Targeted Survivorship', data: true
      }
    });
    if (result === true)
      await this.gotoSurvivorship();
  }

  /**
   * The action to perform when the user selects the Survivorship option
   */
  public async gotoSurvivorship() {
    gotoPost('/DataImport/Survivorship', this._options.verificationToken, { eventId: this.latestEventId, goldenIsNullOption: 'highestNonNull', goldenIsNotNullOption: 'highestNonNull' });
  }

  /**
   * The action to perform when the user selects the Servicer Loan ID Override option
   * 
   * @param loanIds The list of loan ids (should only be one)
   */
  private async onServicerLoanIdOverride(loanIds: number[]) {
    if (loanIds.length !== 1) return;

    const request = await window.$modal<IServicerLoanIdOverride>({
      title: 'Servicer Loan ID Override',
      component: ServicerLoanIdOverrideModal,
      width: 550,
      props: {
        loanId: loanIds[0]
      }
    });

    if (request === undefined) return;

    try {
      window.$loading('Overriding Servicer Loan ID');
      const helper = new AjaxHelper('/InventoryBrowser/Browser', this._options.verificationToken, '', null);
      await helper.postData('', {
        loanId: request.loanId,
        oldServicerLoanId: request.oldServicerLoanId,
        newServicerLoanId: request.newServicerLoanId
      }, { handler: 'OverrideServicerLoanId' });

      window.$loading();
      await window.$alert.success('Servicer Loan ID overridden successfully.');
      location.reload();
    } catch (e: any) {
      window.$loading();
      handleErrorResponse(e, window.$alert, window.$log);
    } finally {
      window.$loading();
    }
  }

  /**
   * The action to perform when the user selects the Survivorship Merge option
   * 
   * @param loanIds The list of loan ids to merge
   */
  private onSurvivorshipMerge(loanIds: number[]) {
    gotoPost('/DataImport/Survivorship', this._options.verificationToken, { loanList: loanIds.join(',') });
  }
}